import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import "./RunBatchProcessing.scss";
import Button from "../../../../components/button/Button";
import DatePicker from "../../../../components/datepicker/DatePicker";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import RadioButton from "../../../../components/radio-button/RadioButton";
import { Dialog } from "../../../../components/dialog/Dialog";
import { RunAI, Warning } from "../../../../components/svg/svgImageList";
import Svg from "../../../../components/svg/Svg";
import useThemeService from "../../../../services/theme-service/ThemeService";
import { translationService } from "../../../../services/translation/TranslationService";
import {
  RunVideoAnalysis,
  RunVideoAnalysisFromAssignedProject,
} from "../../../../services/authorization/Permissions";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { batchVideoAnalysisService } from "../../../../services/batch-processing/BatchVideoAnalysisService";
import { currentUserService } from "../../../../services/user/CurrentUserStorage";
import { RunVideoAnalysisServiceModel } from "../../../../services/inspection/RunVideoAnalysisService";
import useModal from "../../../../components/dialog/useModal";
import { OrganizationSubscriptionPlansStorageModel } from "../../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { api } from "../../../../services/api/ApiService";
import { SubscriptionPlanUsageStorageModel } from "../../../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import Checkbox from "../../../../components/checkbox/Checkbox";
import InformationBeforeAction from "../../../../components/data-components/InformationBeforeAction";
import { getLocalPrice } from "../../../../helpers/MathHelpers";
import BatchProcessingSummary from "./BatchProcessingSummary";
import { BatchProcessingInspectionOption } from "../../../../generated/VideoAnalysis";

interface RunBatchVideoAnalysisProps {
  selectedSewerObjects: string[];
}

export default observer(function RunBatchVideoAnalysis(props: RunBatchVideoAnalysisProps) {
  const { themeClass } = useThemeService();
  const { toggleModal, isVisible } = useModal();

  const [subscriptionPlanUsageStorage] = useState(() =>
    SubscriptionPlanUsageStorageModel.create({ autoLoad: false }, { api })
  );

  const [runVideoAnalysisService] = useState(() => RunVideoAnalysisServiceModel.create({}, {}));

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  const isRunBatchVideoAnalysisDisabled = useMemo(
    () =>
      !permissionsService.userHasOneOfThePermissions([
        RunVideoAnalysis,
        RunVideoAnalysisFromAssignedProject,
      ]) ||
      props.selectedSewerObjects.length <= 0 ||
      !currentUserService.currentUser.currentOrganization.hasPaidSubscription,
    [props.selectedSewerObjects.length]
  );

  const onRunAnalysisClick = useCallback(async () => {
    const videoCallsUsage =
      await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasNotDismissed(
        videoCallsUsage,
        batchVideoAnalysisService.totalCalls
      )
    ) {
      await batchVideoAnalysisService.triggerBatchAnalysis();
      return;
    }

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasPreviouslyDismissed(
        videoCallsUsage,
        batchVideoAnalysisService.totalCalls
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await batchVideoAnalysisService.triggerBatchAnalysis();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasNotDismissed(
        videoCallsUsage,
        batchVideoAnalysisService.totalCalls
      )
    ) {
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInThisBillingCycle(
        videoCallsUsage,
        batchVideoAnalysisService.totalCalls
      )
    ) {
      await batchVideoAnalysisService.triggerBatchAnalysis();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInPreviousBillingCycle(
        videoCallsUsage,
        batchVideoAnalysisService.totalCalls
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleModal();
      return;
    }
  }, [
    organizationSubscriptionPlansStorage,
    runVideoAnalysisService,
    subscriptionPlanUsageStorage,
    toggleModal,
  ]);

  const handleVACallsExceededConfirmationAction = useCallback(async () => {
    if (runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected) {
      runVideoAnalysisService.disableVACallsExceededWarningMessageForOrganization();
    }
    toggleModal();
    await batchVideoAnalysisService.triggerBatchAnalysis();
  }, [runVideoAnalysisService, toggleModal]);

  return (
    <>
      <Button
        id="run-batch-ai"
        onClick={() => batchVideoAnalysisService.showRunBatchModal(props.selectedSewerObjects)}
        disabled={isRunBatchVideoAnalysisDisabled}
        icon={{
          svg: RunAI,
          textKey: "RunVideoAnalysisButton.run",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-video"]),
        }}
      />
      <Dialog
        titleKey="damages.RunBatchVideoAnalysis.title"
        titleContext={{ sewerObjectsCount: props.selectedSewerObjects.length }}
        sidePosition
        isVisible={batchVideoAnalysisService.displayModal}
        onHide={batchVideoAnalysisService.hideRunBatchModal}
        buttonOk={{
          id: "run-video-analysis",
          textKey: "damages.RunBatchVideoAnalysis.run-button",
          disabled: batchVideoAnalysisService.disabledRunAnalysis,
          action: onRunAnalysisClick,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: batchVideoAnalysisService.hideRunBatchModal,
        }}
      >
        <section className="run-batch-analysis-container">
          <h4>
            {translationService.translate("damages.RunBatchVideoAnalysis.message", {
              sewerObjectsCount: props.selectedSewerObjects.length,
            })}
          </h4>
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.relevant-inspections"
            name="batch-option"
            onChange={(e) =>
              batchVideoAnalysisService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchVideoAnalysisService.runAnalysisOption ===
              BatchProcessingInspectionOption.RelevantInspections
            }
            value={String(BatchProcessingInspectionOption.RelevantInspections)}
          />
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.starting-with-date.label"
            name="batch-option"
            onChange={(e) =>
              batchVideoAnalysisService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchVideoAnalysisService.runAnalysisOption ===
              BatchProcessingInspectionOption.StartingWithDate
            }
            value={String(BatchProcessingInspectionOption.StartingWithDate)}
          >
            <DatePicker
              className="starting-with-date"
              value={batchVideoAnalysisService.startingWithDate}
              onChange={(date) =>
                batchVideoAnalysisService.changeStartingWithDate(date as Date | null)
              }
            />
          </RadioButton>
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.all-inspections.label"
            name="batch-option"
            onChange={(e) =>
              batchVideoAnalysisService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchVideoAnalysisService.runAnalysisOption ===
              BatchProcessingInspectionOption.AllInspections
            }
            value={String(BatchProcessingInspectionOption.AllInspections)}
          />
        </section>
        <section className="run-batch-analysis-container">
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.exclude-done-analysis.label"
            onChange={(e) => batchVideoAnalysisService.handleRedoExistingAnalysisChanged(false)}
            checked={!batchVideoAnalysisService.redoExistingAnalysis}
            value="exclude-done-analysis"
          />
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.redo-analysis.label"
            onChange={(e) => batchVideoAnalysisService.handleRedoExistingAnalysisChanged(true)}
            checked={batchVideoAnalysisService.redoExistingAnalysis}
            value="redo-analysis"
          />
        </section>
        <section className="run-batch-analysis-container">
          {batchVideoAnalysisService.isLoading &&
            batchVideoAnalysisService.isStartingWithDateNeeded && (
              <div className="warning">
                <Svg svg={Warning} />
                <p>
                  {translationService.translate(
                    "damages.BatchProcessing.summary.starting-date.required"
                  )}
                </p>
              </div>
            )}
          {batchVideoAnalysisService.isLoading &&
            !batchVideoAnalysisService.isStartingWithDateNeeded && <LoadingSpinner spinnerOnly />}
          {!batchVideoAnalysisService.isLoading && (
            <BatchProcessingSummary
              sewerObjectsCount={batchVideoAnalysisService.batchSummary!.objectsCount}
              inspectionsCount={batchVideoAnalysisService.batchSummary!.inspectionsCount}
              analysingInspectionsCount={
                batchVideoAnalysisService.batchSummary!.analysingInspectionsCount
              }
              videoAnalysisCount={batchVideoAnalysisService.batchSummary!.videoAnalysisCount}
              videoSegmentationCount={0}
            />
          )}
        </section>
      </Dialog>
      <InformationBeforeAction
        isVisible={isVisible}
        titleKey="damages.RunBatchVideoAnalysis.exceeded-va-calls.title"
        buttonOk={{
          id: "run-va-ok",
          textKey: "damages.RunBatchVideoAnalysis.run-button",
          action: handleVACallsExceededConfirmationAction,
        }}
        toggleModal={toggleModal}
      >
        <>
          <div>
            {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.text", {
              extraVACallPrice: getLocalPrice(
                organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice
              ),
            })}
            <Link to={"/subscription/plans"}>
              {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.plan")}
            </Link>
            {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.price", {
              extraVACallPrice: getLocalPrice(
                organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice
              ),
            })}
          </div>
          <div>
            {translationService.translate(
              "damages.RunBatchVideoAnalysis.exceeded-va-calls.total-extra-calls",
              {
                numberOfCalls: subscriptionPlanUsageStorage.getNumberOfExtraCallsAboveLimit(
                  batchVideoAnalysisService.totalCalls
                ),
              }
            )}
          </div>
          <div>
            <span className="bolded-text">
              {translationService.translate(
                "damages.RunBatchVideoAnalysis.exceeded-va-calls.total-cost",
                {
                  totalCost: getLocalPrice(
                    organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice *
                      subscriptionPlanUsageStorage.getNumberOfExtraCallsAboveLimit(
                        batchVideoAnalysisService.totalCalls
                      )
                  ),
                }
              )}
            </span>
          </div>
        </>
        <Checkbox
          className="run-ai-warning-message-checkbox"
          checked={runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected}
          onChange={runVideoAnalysisService.toggleIsDoNotShowThisPopupAgainSelected}
          labelKey="general.dont-show-this-message-again"
        />
      </InformationBeforeAction>
    </>
  );
});
